import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type Configuration,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser'
// ---------------------------------------------------------------------------
// Microsoft (Azure AD) SSO for interactive user sign-in.
//
// Required env vars (set in .env):
//   VITE_AZURE_TENANT_ID     — Azure AD tenant ID
//   VITE_AZURE_CLIENT_ID     — SPA app registration client ID
//   VITE_AZURE_REDIRECT_URI  — Registered SPA redirect URI (defaults to origin)
//
// Databricks Azure resource ID used when requesting a user access token for
// Databricks APIs (Genie, Jobs, etc.).
//   https://learn.microsoft.com/azure/databricks/dev-tools/api/latest/aad/
// ---------------------------------------------------------------------------

const DATABRICKS_RESOURCE_ID = '2ff814a6-3304-4ab8-85cb-cd0e6f879c1d'
const DATABRICKS_SCOPE = `${DATABRICKS_RESOURCE_ID}/.default`

const tenantId = import.meta.env.VITE_AZURE_TENANT_ID
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID

const msalConfig: Configuration = {
  auth: {
    clientId: clientId ?? '',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    redirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    iframeHashTimeout: 10000,
    windowHashTimeout: 10000,
  },
}

export const msalInstance = new PublicClientApplication(msalConfig)

let initialized = false

async function ensureInitialized(): Promise<void> {
  if (!initialized) {
    await msalInstance.initialize()
    initialized = true
  }
}

export async function loginWithMicrosoft(): Promise<AccountInfo> {
  await ensureInitialized()
  // 'User.Read' shares Microsoft Graph as its resource/audience with the
  // basic OIDC scopes, so it can be requested together in the same token
  // request -- this consents to the profile-photo lookup up front.
  const result = await msalInstance.loginPopup({
    scopes: ['openid', 'profile', 'email', 'User.Read'],
  })
  msalInstance.setActiveAccount(result.account)

  // The Databricks resource is a separate audience, so it needs its own
  // token request and can't be folded into the loginPopup call above.
  // Acquire (and cache) it now, as part of this same sign-in flow, so
  // that consenting to Databricks access happens once here rather than
  // as a surprise popup the first time some later page needs it -- every
  // getDatabricksAccessToken() call for the rest of the session can then
  // be satisfied silently from the cached/refreshable token. Best-effort:
  // if this fails (e.g. a popup blocker), sign-in still succeeds and the
  // existing silent-then-popup fallback in acquireAccessToken still
  // applies wherever a Databricks token is actually needed.
  try {
    await acquireAccessToken([DATABRICKS_SCOPE])
  } catch (error) {
    console.warn(
      'Could not pre-acquire a Databricks access token at sign-in; it will be requested again when needed.',
      error
    )
  }

  return result.account
}

export async function getActiveAccount(): Promise<AccountInfo | null> {
  await ensureInitialized()
  return msalInstance.getActiveAccount()
}

/**
 * Returns true when the app has recorded that the user authenticated via
 * Microsoft SSO and MSAL still has an active account. This prevents a
 * stale localStorage entry (e.g. prism_username set by a dev bypass) from
 * granting access.
 *
 * Note: this intentionally does NOT check the allowlist. The allowlist
 * controls which features are visible (via RequireDatabricks), not whether
 * the user can sign in at all. Gating authentication on the allowlist
 * caused non-allowlisted users to be stuck on the sign-in page after
 * completing SSO.
 */
export async function isMsalAuthenticated(): Promise<boolean> {
  const authMethod = localStorage.getItem('prism_auth_method')
  if (authMethod !== 'microsoft') return false
  const account = await getActiveAccount()
  return account !== null
}

/**
 * Synchronous check used for the initial render before MSAL has initialized.
 * Only trusts the localStorage markers; the async validation should run next.
 */
export function hasMsalAuthMarker(): boolean {
  return localStorage.getItem('prism_auth_method') === 'microsoft'
}

export function clearPrismAuth(): void {
  localStorage.removeItem('prism_auth_method')
  localStorage.removeItem('prism_username')
  localStorage.removeItem('prism_display_name')
  localStorage.removeItem('prism_allowlisted')
}

/**
 * Clears MSAL's locally cached tokens for the given account (e.g. after
 * rejecting a sign-in that isn't on the SSO allowlist), without triggering
 * a full interactive Azure AD sign-out. The account's session with
 * Microsoft/other apps is left untouched -- only this app's local cache is
 * cleared, so a silent re-acquisition can't hand back a token for it.
 */
export async function removeMsalAccount(account: AccountInfo): Promise<void> {
  await ensureInitialized()
  await msalInstance.clearCache({ account })
}

/**
 * Acquire an Azure AD access token for the given scopes on behalf of the
 * currently signed-in Microsoft account. Tries a silent token acquisition
 * first; if the user must re-authenticate or consent to the requested
 * scope(s), falls back to a popup.
 *
 * Returns `null` when no MSAL account is active.
 */
async function acquireAccessToken(scopes: string[]): Promise<string | null> {
  const account = await getActiveAccount()
  if (!account) {
    return null
  }

  const request = { account, scopes }

  let result: AuthenticationResult

  try {
    result = await msalInstance.acquireTokenSilent(request)
  } catch (error) {
    if (
      error instanceof InteractionRequiredAuthError ||
      (error instanceof Error &&
        error.message.includes('monitor_window_timeout'))
    ) {
      result = await msalInstance.acquireTokenPopup(request)
    } else {
      throw error
    }
  }

  return result.accessToken
}

/**
 * Acquire an Azure AD access token for the Databricks resource on behalf of
 * the currently signed-in Microsoft account.
 */
export function getDatabricksAccessToken(): Promise<string | null> {
  return acquireAccessToken([DATABRICKS_SCOPE])
}

/**
 * Acquire an Azure AD access token for the Microsoft Graph resource (e.g. to
 * read the signed-in user's profile photo).
 */
export function getGraphAccessToken(): Promise<string | null> {
  return acquireAccessToken(['User.Read'])
}
